import React from 'react';
import { ArrowRight, Inbox } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { PRIORITY_STYLE, HEALTH_STYLE, money } from './creditApi';

export const PriorityPill = ({ p }) => (
  <span className={cn('inline-block px-2 py-0.5 rounded text-[10px] font-extrabold tracking-wider', PRIORITY_STYLE[p] || PRIORITY_STYLE.NORMAL)}>{p}</span>
);

export const HealthPill = ({ h }) => (
  <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide ring-1', HEALTH_STYLE[h] || HEALTH_STYLE.WAITING)}>{h}</span>
);

const COLS = 'grid-cols-[84px_minmax(150px,1.1fr)_minmax(220px,2fr)_minmax(120px,1fr)_100px_110px_120px_100px_90px_120px]';
const COLS_LG = 'lg:grid-cols-[84px_minmax(150px,1.1fr)_minmax(220px,2fr)_minmax(120px,1fr)_100px_110px_120px_100px_90px_120px]';

export const ActionQueue = ({ tasks, onOpen, onQuickAction, emptyLabel }) => (
  <div className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid="action-queue">
    <div className={cn('hidden lg:grid gap-3 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100', COLS)}>
      <span>Priority</span><span>Client</span><span>Action required</span><span>Stage</span><span>Lender</span><span>Broker</span><span>Credit owner</span><span>Waiting</span><span>Due</span><span className="text-right">Action</span>
    </div>
    {tasks.length === 0 && (
      <div className="px-6 py-14 text-center text-slate-500" data-testid="queue-empty">
        <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-300" />
        <div className="font-semibold text-slate-700">{emptyLabel || 'Nothing to do right now'}</div>
        <div className="text-xs mt-1">New actions appear here automatically as files move through the Game Plan.</div>
      </div>
    )}
    {tasks.map((t, i) => (
      <div
        key={t.id}
        onClick={() => onOpen(t)}
        data-testid={`queue-row-${t.deal_id}-${t.key.replace(/[:]/g, '-')}`}
        className={cn('grid grid-cols-1 lg:gap-3 px-4 py-3 items-center cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors',
          COLS_LG, t.priority === 'URGENT' && 'border-l-[3px] border-l-rose-500', t.priority === 'HIGH' && 'border-l-[3px] border-l-amber-400', t.priority === 'NORMAL' && 'border-l-[3px] border-l-transparent')}
        style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}
      >
        <div className="flex items-center justify-between lg:block mb-1 lg:mb-0"><PriorityPill p={t.priority} /><span className="lg:hidden text-xs text-slate-500">{t.due_label}</span></div>
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate">{t.client_name}</div>
          <div className="text-[11px] text-slate-500 tabular-nums">{money(t.loan_amount)}{t.loan_type ? ` · ${t.loan_type}` : ''}</div>
        </div>
        <div className="min-w-0">
          <div className="text-[15px] font-bold text-slate-900 leading-tight truncate" data-testid={`task-label-${t.deal_id}`}>{t.label}</div>
          {t.detail && <div className="text-[11px] text-slate-500 truncate">{t.detail}</div>}
        </div>
        <div className="text-xs text-slate-700 truncate"><span className="lg:hidden text-slate-400">Stage · </span>{t.stage_label}</div>
        <div className="text-xs text-slate-700 truncate hidden lg:block">{t.lender || '—'}</div>
        <div className="text-xs text-slate-700 truncate hidden lg:block">{t.broker || '—'}</div>
        <div className={cn('text-xs truncate hidden lg:block', t.owner ? 'text-slate-700' : 'text-rose-600 font-semibold')}>{t.owner || 'Unassigned'}</div>
        <div className="text-xs text-slate-600 tabular-nums hidden lg:block">{t.waiting_time}</div>
        <div className={cn('text-xs font-bold tabular-nums hidden lg:block', t.overdue ? 'text-rose-600' : 'text-slate-800')}>{t.due_label}</div>
        <div className="lg:text-right mt-2 lg:mt-0">
          <button
            onClick={(e) => { e.stopPropagation(); onQuickAction(t); }}
            data-testid={`task-action-${t.deal_id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-bold hover:bg-slate-700 transition-colors"
          >
            {t.verb} <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    ))}
  </div>
);

export const WaitingTable = ({ items, onOpen, onReceived }) => (
  <div className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid="waiting-table">
    <div className="hidden lg:grid grid-cols-[minmax(150px,1.2fr)_110px_minmax(220px,2fr)_110px_90px_120px_120px_140px] gap-3 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100">
      <span>Client</span><span>Waiting on</span><span>What we need</span><span>Requested</span><span>Days</span><span>Next chase</span><span>Owner</span><span className="text-right">Action</span>
    </div>
    {items.length === 0 && <div className="px-6 py-12 text-center text-sm text-slate-500" data-testid="waiting-empty">Nothing is waiting on anyone else.</div>}
    {items.map((w) => (
      <div key={w.deal_id} onClick={() => onOpen(w)} data-testid={`waiting-row-${w.deal_id}`}
        className="grid grid-cols-1 lg:grid-cols-[minmax(150px,1.2fr)_110px_minmax(220px,2fr)_110px_90px_120px_120px_140px] lg:gap-3 px-4 py-3 items-center border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer">
        <div className="min-w-0"><div className="font-semibold text-slate-900 truncate">{w.client_name}</div><div className="text-[11px] text-slate-500">{money(w.loan_amount)} · {w.lender || 'No lender'} · {w.stage_label}</div></div>
        <div><span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-bold">{w.party_label}</span></div>
        <div className="text-sm text-slate-800 truncate">{w.what}</div>
        <div className="text-xs text-slate-600 hidden lg:block">{new Date(w.requested_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</div>
        <div className={cn('text-xs font-bold tabular-nums', w.days_waiting >= 3 ? 'text-rose-600' : 'text-slate-800')}>{w.days_waiting}d</div>
        <div className="text-xs text-slate-600 hidden lg:block">{w.next_chase_at ? new Date(w.next_chase_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '—'}</div>
        <div className="text-xs text-slate-700 hidden lg:block">{w.owner || <span className="text-rose-600 font-semibold">Unassigned</span>}</div>
        <div className="lg:text-right mt-2 lg:mt-0">
          <button onClick={(e) => { e.stopPropagation(); onReceived(w); }} data-testid={`waiting-received-${w.deal_id}`}
            className="px-3 py-1.5 rounded-full border border-slate-300 text-xs font-bold text-slate-800 hover:bg-slate-900 hover:text-white transition-colors">Received → back to Credit</button>
        </div>
      </div>
    ))}
  </div>
);
